import { useEffect, useState } from "react";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { PrimaryHeaderAction, ThemeSettings } from "../../types/theme.js";
import { close, hamburger } from "../../icons/svg.js";
import { getLinkProps } from "../../client-utils/getLinkProps.js";
import { Drawer } from "../../internal-components/drawer/drawer.js";

interface Props {}

const Header: React.FC<Props> = () => {
  const store = useStore<ThemeSettings>();
  const loggedIn = store.context.loggedIn;
  const pathname = store.context.url.pathname;
  const primaryAction = store.themeSettings.primaryHeaderAction;
  const mainMenu = store.themeSettings.mainMenu || [];
  const logo = store.site.logo;

  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");

    // Function to handle viewport change
    const handleViewportChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        if (menuOpen) {
          document.body.classList.add("freeze");
        } else {
          document.body.classList.remove("freeze");
        }
      }
    };

    // Run on mount to check initial state
    handleViewportChange(mediaQuery);

    // Add event listener
    mediaQuery.addEventListener("change", handleViewportChange);

    return () => {
      mediaQuery.removeEventListener("change", handleViewportChange);
    };
  }, [menuOpen]);

  return (
    <div className="header">
      <header className="top-bar">
        <div className="mw hs m-auto">
          <div className="content">
            <div className="logo">
              <a href="/" aria-label="Gå til forsiden">
                {logo && (
                  <img
                    src={transformImageUrl(logo, { width: 400 })}
                    alt={store.site.name}
                  />
                )}
              </a>
            </div>
            <div className="navigation">
              <ul>
                <ButtonGroup
                  loggedIn={loggedIn}
                  redirect={pathname}
                  primaryAction={primaryAction}
                />
              </ul>

              <button
                className="menu-button"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? close : hamburger}
              </button>
            </div>
          </div>
        </div>
      </header>
      <Drawer
        open={menuOpen}
        position="right"
        onClose={() => setMenuOpen(false)}
        className="main-menu-drawer"
      >
        <div className="blocks">
          <ul className="mobile actions">
            <ButtonGroup
              loggedIn={loggedIn}
              redirect={pathname}
              primaryAction={primaryAction}
            />
          </ul>
          {mainMenu.map((item, index) => {
            return (
              <ul key={index} className="block">
                {(item.links || []).map((block, blockIndex) => {
                  if (!block.link) {
                    return null;
                  }

                  return (
                    <li key={blockIndex}>
                      <a href={block.link.url}>
                        <span className="b1 guide-number">
                          {block.guideNumber}
                        </span>
                        <div>
                          <span className="h6 link-title">
                            {block.link.title}
                          </span>
                          {block.subtext && (
                            <span className="b2 subtext">{block.subtext}</span>
                          )}
                        </div>
                      </a>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </div>
      </Drawer>
    </div>
  );
};

interface ButtonGroupProps {
  loggedIn: boolean;
  redirect?: string;
  primaryAction?: PrimaryHeaderAction;
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({
  loggedIn,
  redirect,
  primaryAction,
}) => {
  return (
    <>
      {loggedIn ? (
        <>
          <li>
            <a href="/logg-ut" className="button button-primary">
              Logg ut
            </a>
          </li>
          {primaryAction?.loggedInLink && (
            <li>
              <a
                {...getLinkProps(primaryAction.loggedInLink)}
                className="button button-secondary"
              >
                {primaryAction.loggedInLink.title}
              </a>
            </li>
          )}
        </>
      ) : (
        <>
          <li>
            <a
              href={`/logg-inn?redirect=${redirect}`}
              className="button button-primary"
            >
              Logg inn
            </a>
          </li>
          {primaryAction?.loggedOutLink && (
            <li>
              <a
                {...getLinkProps(primaryAction.loggedOutLink)}
                className="button button-secondary"
              >
                {primaryAction.loggedOutLink.title}
              </a>
            </li>
          )}
        </>
      )}
    </>
  );
};

export default Header;
